import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material'
import { Mutation, Query } from '@promoboxx/graphql-gateway-types'
import { Formik, FormikProps } from 'formik'
import I18n from 'i18n-js'
import React, { useEffect, useMemo, useRef } from 'react'

import { DigitalAssetDetails } from '@src/components/downloadables/DigitalAssetDialog'
import Media from '@src/components/downloadables/Media'
import { CREATE_ASSET_SHARE_MUTATION } from '@src/components/shareAssetDialog/queries'
import { useShareAssetDialog } from '@src/components/shareAssetDialog/useShareAssetDialog'
import MobileFullScreenDialog from '@src/components/shared/MobileFullScreenDialog'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'

import { GET_ASSET_SHARE_QUERY } from '../shareAssetDialog/queries'

const ShareAssetDialog: React.FC = () => {
  const { closeShareAssetDialog, getShareAssetParams, publish } =
    useShareAssetDialog()
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const { id, type } = getShareAssetParams()
  const { data } = useQuery<Query>(GET_ASSET_SHARE_QUERY, {
    variables: {
      assetId: id,
    },
    skip: !id || !type,
  })

  const downloadUrl = data?.getAsset?.digital_asset?.download_url || ''

  const retailer = useMemo(() => {
    return ensureCurrentRetailerAccount.retailers?.find(
      (x) => x.brand?.id === data?.getAsset?.brand_id,
    )
  }, [data?.getAsset?.brand_id, ensureCurrentRetailerAccount.retailers])

  const [createAssetShare] = useMutation<Mutation>(CREATE_ASSET_SHARE_MUTATION)
  const formikRef = useRef<FormikProps<{
    resourceId: string
    assetId: string
  }> | null>(null)
  const socialPreviewContainer = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (retailer && id) {
      formikRef.current?.setFieldValue('resourceId', retailer.id, false)
      formikRef.current?.setFieldValue('assetId', id, false)
    }
  }, [id, retailer])

  function handleClose() {
    closeShareAssetDialog()
  }
  return (
    <Formik<{
      resourceId: string
      assetId: string
    }>
      onSubmit={async (values, formikActions) => {
        if (!values.resourceId || !values.assetId || !id || !type) {
          return
        }
        formikActions.setSubmitting(true)
        try {
          const result = await createAssetShare({
            variables: {
              resource_id: values.resourceId,
              asset_share: {
                retailer_id: values.resourceId,
                asset_id: values.assetId,
              },
            },
          })
          if (result.data?.createShare) {
            publish('shareAssetDialogUpdated', {
              id,
              type,
            })
          }
          handleClose()
        } catch (e) {
          console.error(e)
        }
        formikActions.setSubmitting(false)
      }}
      initialValues={{
        resourceId: '',
        assetId: '',
      }}
    >
      {(formikProps) => {
        formikRef.current = formikProps

        return (
          <MobileFullScreenDialog
            open={!!id || !!type}
            onClose={() => {
              handleClose()
            }}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>{data?.getAsset?.name}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} order={{ xs: 2, sm: 1 }}>
                  {type === 'tiktok' && (
                    <div
                      ref={socialPreviewContainer}
                      style={{ position: 'sticky', top: 0, left: 0 }}
                    />
                  )}
                  {data?.getAsset?.digital_asset && (
                    <Media
                      digitalAsset={data?.getAsset?.digital_asset}
                      width={700}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={6} order={{ xs: 1, sm: 2 }}>
                  {data?.getAsset?.digital_asset && (
                    <DigitalAssetDetails
                      digitalAsset={{
                        ...data.getAsset.digital_asset,
                        description: data?.getAsset?.description || '',
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  handleClose()
                }}
              >
                {I18n.t('shared.cancel')}
              </Button>

              <Button
                variant="contained"
                color="primary"
                data-testid="downloadBtn"
                href={downloadUrl}
                target="_blank"
                onClick={() => {
                  formikProps.submitForm()
                }}
              >
                {I18n.t('downloadables.download')}
              </Button>
            </DialogActions>
          </MobileFullScreenDialog>
        )
      }}
    </Formik>
  )
}

export default ShareAssetDialog
